import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";

import { GetContactsMatchingPhoneNumberRequest } from "@/Common/Api/CallCenter/Requests/GetContactsMatchingPhoneNumberRequest";
import { CallCenterUserState } from "@/Common/Api/CallCenter/Responses/CallCenterUserState";
import { ContactMatchingPhoneNumberResult } from "@/Common/Api/CallCenter/Responses/ContactMatchingPhoneNumberResult";
import { GetContactsMatchingPhoneNumberResponse } from "@/Common/Api/CallCenter/Responses/GetContactsMatchingPhoneNumberResponse";
import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { CallCenterHelper } from "@/Common/Helpers/CallCenterHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { CallCenterCallDirection } from "@/Common/Models/CallCenter/CallCenterCallDirection";
import { CallCenterCurrentState } from "@/Common/Models/CallCenter/CallCenterCallState";
import { CallCenterCallType } from "@/Common/Models/CallCenter/CallCenterCallType";
import { CallIdAndNumber } from "@/Common/Models/CallCenter/CallIdAndNumber";
import { CallMatches } from "@/Common/Models/CallCenter/CallMatches";
import { Guid } from "@/Common/Models/Guid";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import * as ServerOperations from "@/Common/Operations/operations.server";
import ContactMatchingPhoneNumberResultSorter from "@/Common/Utilities/ContactMatchingPhoneNumberResultSorter";
import { displayToast, MessageToast } from "../Toast/Toast";
import { RecallStatusTypeLookup } from "./CallCenter.types";
import { CreatePatientCallCommunicationRequest, CreatePatientCallCommunicationResponse } from "./PatientCallCommunication.types";
import { ViewPatientMatches } from "./ViewPatientMatches";

import styles from "./WindowContainer.module.scss";

interface IWindowContainerProps {
    userState: CallCenterUserState
}

const EmptyCall = new CallIdAndNumber(null, null);
const EmptyMatches = new CallMatches(EmptyCall, null);

function WindowContainer(props: Readonly<IWindowContainerProps>): JSX.Element {
    const [closedCall, setClosedCall] = useState<CallIdAndNumber>(EmptyCall);
    const [loading, setLoading] = useState(false);
    const [loadedErrors, setLoadedErrors] = useState<ErrorMessage[]>(null);
    const [shownErrorsCall, setShownErrorsCall] = useState<CallIdAndNumber>(EmptyCall);
    const [matchedResult, setMatchedResult] = useState<CallMatches>(EmptyMatches);
    const [creatingCallLog, setCreatingCallLog] = useState(false);
    const [loggedClickToDialCall, setLoggedClickToDialCall] = useState<CallIdAndNumber>(EmptyCall);
    const [profilePictureVersion, setProfilePictureVersion] = useState<Guid>(null);

    function shouldLoadCall(state: CallCenterUserState): boolean {
        if (state == null
            || state.CallDirection !== CallCenterCallDirection.Inbound
            || state.CallType === CallCenterCallType.ProfessionalCall
            || IsNullOrWhiteSpace(state.CallId)
            || IsNullOrWhiteSpace(state.CallerNumber)
            || !CallCenterHelper.is10DigitPhoneNumber(state.CallerNumber)) {
            return false;
        }
        switch (state.CurrentState) {
            case CallCenterCurrentState.Ringing:
            case CallCenterCurrentState.InCall:
            case CallCenterCurrentState.OnHold:
                return true;
            default:
                return false;
        }
    }

    function shouldShowCall(state: CallCenterUserState): boolean {
        if (state == null
            || state.CallDirection !== CallCenterCallDirection.Inbound
            || IsNullOrWhiteSpace(state.CallId)
        ) {
            return false;
        }
        switch (state.CurrentState) {
            case CallCenterCurrentState.InCall:
            case CallCenterCurrentState.OnHold:
                return true;
            default:
                return false;
        }
    }

    function onCloseClicked(): void {
        setClosedCall(currentCall);
    }

    function onRetry(): void {
        setMatchedResult(EmptyMatches);
    }

    function onSizeChanged(isResize: boolean = false): void {
        function resizeCallback(): void {
            // get the size of the window and move into viewport if its exceeded
            const selfContainer = $("." + styles.PopupWindow);
            if (selfContainer.length == 0) {
                // don't exist yet
                return;
            }
            const container = $(window);
            const width = selfContainer.outerWidth();
            const height = selfContainer.outerHeight();
            const { left, top } = selfContainer.offset();
            const containerWidth = container.outerWidth();
            const containerHeight = container.outerHeight();

            // coordinates are bottom right offsets which are 0,0 at the bottom right
            let right = containerWidth - left - width;
            let bottom = containerHeight - top - height;
            let outOfBounds = false;

            if (right < 0) {
                right = 0;
                outOfBounds = true;
            }
            else if (right + width > containerWidth) {
                right = Math.max(0, containerWidth - width);
                outOfBounds = true;
            }
            if (bottom < 0) {
                bottom = 0;
                outOfBounds = true;
            }
            else if (bottom + height > containerHeight) {
                bottom = Math.max(0, containerHeight - height);
                outOfBounds = true;
            }

            if (outOfBounds) {
                selfContainer
                    .css("top", "auto")
                    .css("left", "auto")
                    .css("right", (right) + "px")
                    .css("bottom", (bottom) + "px");
            }
        }
        if (isResize) {
            resizeCallback();
        }
        else {
            // hack to resize after the react component finishes drawing
            setTimeout(resizeCallback, 50);
        }
    }

    const createCallLog = useCallback(async (patientContactFacetId: Guid): Promise<boolean> => {
        if (creatingCallLog) {
            return;
        }
        setCreatingCallLog(true);
        const { userState } = props;
        const followUpLookupIdAsString: string = userState.CallDirection == CallCenterCallDirection.Inbound
            ? cacheStore.FindLookupByLookupCode(RecallStatusTypeLookup.IncomingCall, RecallStatusTypeLookup.Code).LookupID
            : cacheStore.FindLookupByLookupCode(RecallStatusTypeLookup.OutgoingCall, RecallStatusTypeLookup.Code).LookupID;
        const followUpLookupId: Guid = GuidHelper.AsGuid(followUpLookupIdAsString);
        const request: CreatePatientCallCommunicationRequest = {
            ProviderId: userState.ProviderId,
            CallId: userState.CallId,
            CallDirection: userState.CallDirection,
            CallStart: userState.CallStart,
            CallEnd: userState.CallEnd,
            PatientId: patientContactFacetId,
            Version: 1,
            FollowUpLookupId: followUpLookupId,
            Note: "",
            CallReasons: [],
            EmployeeId: cacheStore.GetCurrentEmployee().ContactFacet_id
        };
        const response = await Client.postAsync<CreatePatientCallCommunicationResponse>(ServerOperations.Server.Myle_CallCenter_CallCommunicationAdd, request);
        setCreatingCallLog(false);
        if (!response.isSuccess) {
            console.error("Error on Call Communication Log", response);
            displayToast(MessageToast.Error, cacheStore.GetString("CallCenter_PatientCallLog_FailureToast"));
            return false;
        }
        return true;
    }, [props, creatingCallLog]);

    const onContactSelected = useCallback(async (contactMatching: ContactMatchingPhoneNumberResult): Promise<boolean> => {
        const patientContactFacetId = contactMatching.Individual.ContactFacetId;
        return createCallLog(patientContactFacetId);
    }, [createCallLog]);

    const defaultPosRight = 30;
    const defaultPosBottom = 60;

    const currentCall: CallIdAndNumber = useMemo(() => (
        new CallIdAndNumber(props.userState?.CallId, props.userState?.CallerNumber)
    ), [props.userState?.CallId, props.userState?.CallerNumber]);

    const isClosedCall = currentCall?.Equals(closedCall);
    const isClickToDial = props.userState?.CallDirection == CallCenterCallDirection.ClickToDial;

    // check if we should log click to dial
    useEffect(() => {
        if (!isClickToDial) {
            return;
        }
        // not enough info to log the call
        if (props.userState.CallId == null || props.userState.PatientId == null) {
            return;
        }
        // not in the call yet
        if (props.userState.CurrentState != CallCenterCurrentState.InCall && props.userState.CurrentState != CallCenterCurrentState.OnHold) {
            return;
        }
        // already logged call
        if (loggedClickToDialCall.Equals(currentCall)) {
            return;
        }
        // log click to dial
        setLoggedClickToDialCall(currentCall);
        createCallLog(props.userState.PatientId);
    }, [props, currentCall, isClickToDial, loggedClickToDialCall, createCallLog]);

    const loadCall = shouldLoadCall(props.userState);
    const showCall = shouldShowCall(props.userState);
    const showPopup = showCall && !isClosedCall;
    const hasErrors = loadedErrors != null;
    const showErrors = showPopup && hasErrors && !shownErrorsCall.Equals(currentCall);

    const loadPatientRAMQStatus = useCallback(async (results: ContactMatchingPhoneNumberResult[]): Promise<void> => {
        for (const result of results) {
            if (result.Individual.ContactFacetType != ContactFacetType.Patient) {
                continue;
            }

            const requestData = {
                Patient: {
                    IndividualID: result.Individual.Contact_id
                },
                CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
            };

            const response = await Client.postAsync<RAMQInfo>(ServerOperations.Server.RAMQ_CheckSubscription, requestData);

            if (!response.isSuccess) {
                console.error(response);
                continue;
            }

            setMatchedResult(cur =>
                cur.WithMatches(
                    cur?.Matches?.update(
                        cur?.Matches?.findIndex(
                            item => item.Individual.ContactFacetId == result.Individual.ContactFacetId
                        ),
                        oldValue => ({ ...oldValue, RAMQRegistrationResponse: response.data })
                    )
                ));
        }
    }, []);

    // load and sort matching number
    useEffect(() => {
        // don't load if already loaded
        if (matchedResult.CallIdAndNumber.Equals(currentCall)) {
            return;
        }

        // don't load if already loading
        if (loading) {
            return;
        }

        // clear results
        setShownErrorsCall(EmptyCall);
        setLoadedErrors(null);

        // return empty matches if we're not supposed to load
        if (!loadCall) {
            setMatchedResult(new CallMatches(currentCall, [].toImmutable()));
            onSizeChanged();
            return;
        }

        function preloadProfilePictures(results: ContactMatchingPhoneNumberResult[]): void {
            const newVersion = GuidHelper.NewGuid();
            setProfilePictureVersion(newVersion);
            for (const result of results) {
                if (result.ProfilePicture != null) {
                    const img = new Image();
                    img.src = apiSettings.buildAppUrl(`api/patient/profile/image/fetch/${result.ProfilePicture.Id}?_nocache=${newVersion}`, { noCache: false, auth: true });
                }
            }
        }

        async function loadResults(phoneCallIdAndNumber: CallIdAndNumber): Promise<void> {
            const request: GetContactsMatchingPhoneNumberRequest = { PhoneNumber: phoneCallIdAndNumber.CallNumber };
            const response = await Client.postAsync<GetContactsMatchingPhoneNumberResponse>(ServerOperations.Server.Myle_CallCenter_GetContactsMatchingPhoneNumber, request);
            if (!response.isSuccess) {
                console.error("Error Calling GetContactsMatchingPhoneNumber.", response.errors, request);
                setMatchedResult(new CallMatches(phoneCallIdAndNumber, null));
                setLoadedErrors(response.errors);
                setShownErrorsCall(EmptyCall);
                setLoading(false);
                onSizeChanged();
                return;
            }

            let sortedResults = null;

            try {
                const sorter = new ContactMatchingPhoneNumberResultSorter(DateTime.now());
                sortedResults = sorter.SortResults(response.data.ContactsMatching);
                setMatchedResult(new CallMatches(phoneCallIdAndNumber, sortedResults.toImmutable()));
                setLoading(false);
                onSizeChanged();
            }
            catch (e) {
                console.error(e);
                setMatchedResult(new CallMatches(phoneCallIdAndNumber, null));
                setLoadedErrors([]);
                setShownErrorsCall(EmptyCall);
                setLoading(false);
                onSizeChanged();
                return;
            }

            preloadProfilePictures(sortedResults);
            loadPatientRAMQStatus(sortedResults);
        }

        // start loading
        setLoading(true);
        setMatchedResult(new CallMatches(EmptyCall, null));
        loadResults(currentCall);
        onSizeChanged();
    }, [currentCall, loading, loadCall, matchedResult, loadPatientRAMQStatus]);

    // detect browser size change to keep popup within window
    useEffect(() => {
        if (!showPopup) {
            return;
        }

        function onResize(): void {
            onSizeChanged(true);
        }
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [showPopup]);

    // add dragging support to window
    useEffect(() => {
        if (!showPopup) {
            return;
        }
        const draggableElement = $("." + CSS.escape(styles.PopupWindow));
        const handle = $("." + CSS.escape(styles.DragHeader));

        function onStartDrag(): void {
            // convert to top left alignment for dragging
            const { left, top } = draggableElement.offset();

            draggableElement
                .css("bottom", "auto")
                .css("right", "auto")
                .css("left", left + "px")
                .css("top", top + "px");

            handle.addClass(styles.DraggingHeader);
        }

        function onStopDrag(): void {
            // convert back to bottom right alignment
            const containerElem = $(window);
            const { left, top } = draggableElement.offset();
            const width = draggableElement.outerWidth();
            const height = draggableElement.outerHeight();
            const containerWidth = containerElem.outerWidth();
            const containerHeight = containerElem.outerHeight();
            const right = containerWidth - width - left;
            const bottom = containerHeight - height - top;

            draggableElement
                .css("top", "auto")
                .css("left", "auto")
                .css("right", (right) + "px")
                .css("bottom", (bottom) + "px");

            handle.removeClass(styles.DraggingHeader);
        }

        const dragOptions = {
            containment: "window",
            handle: handle,
            start: onStartDrag,
            stop: onStopDrag
        };
        draggableElement.draggable(dragOptions);

        return () => draggableElement.draggable("destroy");
    }, [showPopup]);

    // show errors when the popup opens if we haven't shown them before
    useEffect(() => {
        if (!loadedErrors?.any()) {
            return;
        }
        if (!showErrors) {
            return;
        }
        if (shownErrorsCall.Equals(currentCall)) {
            return;
        }
        sessionStore.showPopup(loadedErrors);
        setShownErrorsCall(currentCall);
    }, [loadedErrors, showErrors, shownErrorsCall, currentCall]);

    // clear closedCallID once we've started a new call
    useEffect(() => {
        if (!isClosedCall && !closedCall.Equals(EmptyCall)) {
            setClosedCall(EmptyCall);
        }
    }, [closedCall, isClosedCall]);

    if (showPopup) {
        return (
            <div className={styles.PopupWindow} style={{ right: defaultPosRight, bottom: defaultPosBottom }}>
                <ViewPatientMatches
                    userState={props.userState}
                    callMatches={matchedResult}
                    isMatchError={hasErrors}
                    dragClass={styles.DragHeader}
                    profilePictureVersion={profilePictureVersion}
                    onClose={onCloseClicked}
                    onRetry={onRetry}
                    onSizeChanged={onSizeChanged}
                    onContactSelected={onContactSelected}
                />
            </div>
        );
    }
    return <></>;
}

export { WindowContainer };
